import React, { useState } from "react";
import {
    Radio as RadioIcon, Volume2, VolumeX, Play, Pause,
    MessageSquare, Loader2, Heart, ThumbsUp, ThumbsDown,
} from "lucide-react";
import { useRadio } from "@/components/RadioProvider";
import PerformerOverlay from "@/components/PerformerOverlay";
import TrackTime from "@/components/TrackTime";
import RadioAdBadge from "@/components/RadioAdBadge";

/**
 * RadioPlayer — pure UI consumer (iter 31.14, 23 May 2026).
 *
 * All audio state + the <audio> DOM node live in RadioProvider at the
 * App.js root, ABOVE the route tree. This component is just the button
 * + dropdown the operator sees. It can mount / unmount freely on page
 * navigation — the audio engine keeps playing regardless.
 *
 * Iter 31.26 — adds reaction buttons (love · like · dislike) on the
 * now-playing card. Tap toggles; same value tapped twice clears it.
 * Affects the weighted shuffler (love=3× · like=1.5× · null=1× · dislike=0.1×).
 */

function ReactionRow({ track, onReact }) {
    const r = track?.reaction || null;
    const Btn = ({ value, Icon, label, activeClass }) => {
        const active = r === value;
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    // tap-twice-to-clear
                    onReact(track.id, active ? null : value);
                }}
                data-testid={`radio-react-${value}`}
                aria-pressed={active}
                title={label}
                className={`inline-flex items-center gap-1 px-2 py-1 border font-heading uppercase text-[9px] tracking-[0.18em] transition-colors ${
                    active ? activeClass : "border-ink/25 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                }`}
            >
                <Icon className="w-3 h-3" />
                {label}
            </button>
        );
    };
    return (
        <div className="flex items-center gap-1.5 mt-2" data-testid="radio-reactions">
            <Btn value="love"    Icon={Heart}      label="Love"    activeClass="border-stamp-red bg-stamp-red text-paper" />
            <Btn value="like"    Icon={ThumbsUp}   label="Like"    activeClass="border-stamp-green bg-stamp-green text-paper" />
            <Btn value="dislike" Icon={ThumbsDown} label="Dislike" activeClass="border-ink text-ink bg-paper" />
        </div>
    );
}

export default function RadioPlayer() {
    const [expanded, setExpanded] = useState(false);
    const {
        tracks, trackIdx, currentTrack,
        playing, muted, bulletinBusy, statusLine,
        bulletinPoolSize = 0,
        currentAnnouncer,
        currentPerformerKey = "twenty",
        setMuted, togglePlay, playBulletin, setReaction,
        audioRef,
    } = useRadio();
    const audioEl = audioRef?.current || null;
    // Iter 31.135 — dynamic cadence label.
    const cadenceN = Math.max(2, 6 - Math.floor(Math.max(0, bulletinPoolSize) / 5));
    // Iter 31.136 — performer face. During a bulletin the announcer's
    // portrait shows (rotated server-side). Otherwise the track's
    // computed performer (male/female/nb/group).
    const performerKey = bulletinBusy
        ? (currentAnnouncer || "two")
        : currentPerformerKey;
    const performerPortrait = `/agent-portraits/${performerKey}.png`;

    return (
        <div
            className="relative inline-flex select-none"
            data-testid="radio-player"
        >
            <button
                onClick={() => setExpanded((e) => !e)}
                className={`inline-flex items-center gap-2 border px-2 py-1 font-heading uppercase tracking-[0.22em] text-[10px] hover:border-stamp-red hover:text-stamp-red ${
                    playing
                        ? "border-stamp-red text-stamp-red"
                        : "border-ink/30 text-ink"
                }`}
                title={playing ? "On Air — controls" : "Radio controls"}
                data-testid="radio-toggle-expand"
                aria-expanded={expanded}
                aria-label="Toggle radio controls"
            >
                {/* Iter 31.135b — performer portrait IN the closed pill.
                    40×40, amplitude-bops to the nub audio. Becomes the
                    face of the radio across every page. */}
                <PerformerOverlay
                    imageUrl={performerPortrait}
                    audioElement={audioEl}
                    audioActive={playing}
                    forceReading={bulletinBusy}
                    muted
                    playKey={0}
                    altText={bulletinBusy ? "bulletin announcer — Two" : "performer — Twenty"}
                    className={`w-10 h-10 overflow-hidden rounded-sm flex-shrink-0 border ${
                        playing ? "border-stamp-red/40" : "border-ink/20 opacity-60"
                    }`}
                />
                <span className="hidden sm:inline">{playing ? "On Air" : "Radio"}</span>
                {playing && (
                    <RadioIcon className="w-3 h-3 animate-pulse" strokeWidth={1.6} />
                )}
            </button>
            {expanded && (
                <div
                    className="absolute top-full right-0 mt-1 z-[2147483640] paper-card border border-ink/30 shadow-lg"
                    style={{ minWidth: 320 }}
                    data-testid="radio-controls-dropdown"
                >
                    <div className="flex items-center gap-1 px-3 py-2 border-b border-ink/15">
                        <button
                            onClick={togglePlay}
                            className="px-2 py-1 text-ink-soft hover:text-stamp-red"
                            title={playing ? "Pause" : "Play"}
                            data-testid="radio-play-toggle"
                        >
                            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        {/* Iter 31.135 — skip button removed. Canon:
                            radio is on or off, it can't be skipped. */}
                        <button
                            onClick={() => setMuted((m) => !m)}
                            className="px-2 py-1 text-ink-soft hover:text-stamp-red"
                            title={muted ? "Unmute" : "Mute"}
                            data-testid="radio-mute-toggle"
                        >
                            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={playBulletin}
                            disabled={bulletinBusy}
                            className="px-2 py-1 text-ink-soft hover:text-stamp-red disabled:opacity-50"
                            title="Bulletin now"
                            data-testid="radio-bulletin"
                        >
                            {bulletinBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                        </button>
                        {/* Iter 31.132e — lip-sync performer beside the
                            //ON AIR statusLine. Twenty bops to the nub's
                            own audio amplitude whenever the station is
                            playing. WeakMap cache in PerformerOverlay
                            keeps this graph independent of the /radio
                            page's separate <audio>. */}
                        <PerformerOverlay
                            imageUrl="/agent-portraits/twenty.png"
                            audioElement={audioEl}
                            audioActive={playing}
                            muted
                            playKey={0}
                            altText="performer — Agent Twenty"
                            className="w-7 h-7 overflow-hidden rounded-sm flex-shrink-0 border border-ink/15"
                        />
                        <div className="flex-1 min-w-0 label-typed text-[9px] truncate pl-1">
                            {statusLine || (playing ? "//ON AIR" : "//STANDBY")}
                        </div>
                    </div>
                    {currentTrack && (
                        <div className="px-3 py-2">
                            {/* Iter 31.214a — ad disclosure badge. Visible
                                whenever the current track carries an ad_payload
                                (iter 31.214). Listeners know what they're hearing.
                                Also fires an impression on mount and routes the
                                title-click through the click-tracking endpoint. */}
                            {currentTrack.ad_payload?.advertiser_key && (
                                <RadioAdBadge
                                    track={currentTrack}
                                    data-testid="radio-ad-badge"
                                />
                            )}
                            <div className="label-typed text-[9px]">
                                {currentTrack.ad_payload?.advertiser_key ? "Sponsored" : "Now playing"}
                            </div>
                            <div className="font-heading text-xs text-ink truncate" data-testid="radio-current-track">
                                {currentTrack.title || currentTrack.filename || "Untitled"}
                            </div>
                            <div className="font-body text-[10px] text-ink-soft mt-0.5">
                                Track {trackIdx + 1} of {tracks.length} · bulletin every {cadenceN} songs
                            </div>
                            {/* Iter 31.206 — elapsed / remaining. */}
                            <div className="font-mono text-[10px] text-ink mt-1" data-testid="radio-track-time-wrap">
                                <TrackTime audioElement={audioEl} testId="radio-track-time" />
                            </div>
                            <ReactionRow track={currentTrack} onReact={setReaction} />
                        </div>
                    )}
                    {!currentTrack && tracks.length === 0 && (
                        <div className="px-3 py-2 font-body text-[11px] text-ink-soft leading-snug">
                            No tracks filed yet. Drop an MP3/WAV/M4A into the Shredder.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
